import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminObblighi() {
    const [tab, setTab] = useState("apply"); // apply | history
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [extraRows, setExtraRows] = useState([]); // manually-added rows
    const [selected, setSelected] = useState({}); // {idx: true|false}
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [applyResult, setApplyResult] = useState(null);
    const [error, setError] = useState(null);

    // Manual form state
    const [mPlayer, setMPlayer] = useState("");
    const [mHostTeam, setMHostTeam] = useState("");
    const [mFee, setMFee] = useState(0);
    const [resolvingManual, setResolvingManual] = useState(false);

    // History
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const onUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setLoading(true); setError(null); setPreview(null); setApplyResult(null);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/admin/obblighi/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data);
            const sel = {};
            for (const r of data.rows) sel[`x-${r.idx}`] = r.status === "ready";
            setSelected(sel);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore upload");
        } finally { setLoading(false); }
    };

    const addManual = async () => {
        if (!mPlayer.trim() || !mHostTeam.trim()) {
            alert("Inserisci nome giocatore e squadra");
            return;
        }
        setResolvingManual(true);
        try {
            const { data } = await api.post("/admin/obblighi/resolve", {
                raw_team: mHostTeam.trim(),
                raw_player: mPlayer.trim(),
                fee: Number(mFee) || 0,
            });
            const newIdx = `m-${Date.now()}`;
            const row = { ...data, idx: newIdx };
            setExtraRows((arr) => [...arr, row]);
            setSelected((s) => ({ ...s, [newIdx]: row.status === "ready" }));
            setMPlayer(""); setMHostTeam(""); setMFee(0);
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally { setResolvingManual(false); }
    };

    const removeManual = (idx) => {
        setExtraRows((arr) => arr.filter((r) => r.idx !== idx));
        setSelected((s) => {
            const next = { ...s }; delete next[idx]; return next;
        });
    };

    const toggleRow = (idx) => {
        setSelected((s) => ({ ...s, [idx]: !s[idx] }));
    };

    const toggleAll = (val) => {
        const next = { ...selected };
        for (const r of preview?.rows || []) {
            const key = `x-${r.idx}`;
            if (r.status === "ready") next[key] = val;
        }
        for (const r of extraRows) {
            if (r.status === "ready") next[r.idx] = val;
        }
        setSelected(next);
    };

    const apply = async () => {
        if (!confirm("Confermare l'applicazione dei trasferimenti selezionati? L'operazione modifica rose e budget. Potrai annullare singole operazioni dallo storico.")) return;
        setApplying(true); setError(null);
        try {
            const items = [];
            for (const r of preview?.rows || []) {
                if (!selected[`x-${r.idx}`] || r.status !== "ready") continue;
                items.push({
                    host_team_id: r.host_team_id, owner_team_id: r.owner_team_id,
                    host_entry_id: r.host_entry_id, owner_entry_id: r.owner_entry_id,
                    fee: r.fee, player_name: r.raw_player,
                });
            }
            for (const r of extraRows) {
                if (!selected[r.idx] || r.status !== "ready") continue;
                items.push({
                    host_team_id: r.host_team_id, owner_team_id: r.owner_team_id,
                    host_entry_id: r.host_entry_id, owner_entry_id: r.owner_entry_id,
                    fee: r.fee, player_name: r.raw_player, manual: true,
                });
            }
            if (items.length === 0) { setError("Nessuna riga selezionata"); setApplying(false); return; }
            const { data } = await api.post("/admin/obblighi/apply", { items });
            setApplyResult(data);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally { setApplying(false); }
    };

    const loadHistory = async () => {
        setHistoryLoading(true);
        try {
            const { data } = await api.get("/admin/obblighi/history", { params: { limit: 200 } });
            setHistory(data.history || []);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally { setHistoryLoading(false); }
    };

    useEffect(() => { if (tab === "history") loadHistory(); }, [tab]);

    const rollback = async (id, name) => {
        if (!confirm(`Annullare l'operazione su ${name}? Roster e budget verranno ripristinati.`)) return;
        try {
            await api.post(`/admin/obblighi/rollback/${id}`);
            await loadHistory();
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || "Errore rollback");
        }
    };

    const fmtEur = (n) => n ? `€ ${n.toLocaleString("it-IT")}` : "—";
    const allRows = [...(preview?.rows || []), ...extraRows];
    const keyFor = (r) => typeof r.idx === "string" && r.idx.startsWith("m-") ? r.idx : `x-${r.idx}`;
    const totalSelected = allRows.filter((r) => selected[keyFor(r)] && r.status === "ready").length;
    const totalCash = allRows.reduce((sum, r) => sum + ((selected[keyFor(r)] && r.status === "ready") ? r.fee : 0), 0);

    return (
        <div data-testid="admin-obblighi">
            <div className="op-label text-tff7a1a">// MERCATO · OBBLIGHI DI RISCATTO</div>
            <h2 className="op-heading text-3xl mt-2">Obblighi di Riscatto</h2>

            {/* Tab switcher */}
            <div className="flex gap-1 mt-5 border-b border-t22252a">
                <TabBtn active={tab === "apply"} onClick={() => setTab("apply")} testId="tab-apply">
                    Applica
                </TabBtn>
                <TabBtn active={tab === "history"} onClick={() => setTab("history")} testId="tab-history">
                    Storico & Rollback
                </TabBtn>
            </div>

            {tab === "apply" && (
                <>
                    <p className="text-t8f95a3 text-sm mt-4 max-w-3xl">
                        Carica il file <span className="text-tffffff">.xls</span> e/o aggiungi righe manuali.
                        Cifra 0 = prestito termina, giocatore torna al proprietario. Cifra &gt; 0 = giocatore resta dove sta, squadra attuale paga.
                    </p>

                    {/* Upload */}
                    <div className="op-card p-5 mt-5">
                        <label className="op-label text-[10px] text-tff7a1a mb-2 block">FILE OBBLIGHI</label>
                        <input
                            type="file" accept=".xls,.xlsx,.html"
                            onChange={onUpload}
                            disabled={loading}
                            className="op-mono text-sm text-tc9ccd3 file:bg-t0e1014 file:text-tffffff file:border file:border-t22252a file:px-4 file:py-2 file:mr-3 file:cursor-pointer file:op-label"
                            data-testid="obblighi-upload"
                        />
                        {file && <div className="op-mono text-[10px] text-t5c6370 mt-2">{file.name}</div>}
                    </div>

                    {/* Manual add */}
                    <div className="op-card p-5 mt-4" style={{ borderLeft: "3px solid rgb(var(--tc-2bb6ff))" }}>
                        <div className="op-label text-[10px] text-t2bb6ff mb-3">+ AGGIUNGI RIGA MANUALE (es. Ekitike)</div>
                        <div className="grid sm:grid-cols-4 gap-3">
                            <div>
                                <label className="op-label text-[10px] text-t8f95a3">Giocatore</label>
                                <input
                                    value={mPlayer} onChange={(e) => setMPlayer(e.target.value)}
                                    placeholder="es. Hugo Ekitike"
                                    className="w-full bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-1.5 mt-1 focus:border-t2bb6ff outline-none"
                                    data-testid="manual-player"
                                />
                            </div>
                            <div>
                                <label className="op-label text-[10px] text-t8f95a3">Squadra attuale</label>
                                <input
                                    value={mHostTeam} onChange={(e) => setMHostTeam(e.target.value)}
                                    placeholder="es. Mallorca"
                                    className="w-full bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-1.5 mt-1 focus:border-t2bb6ff outline-none"
                                    data-testid="manual-team"
                                />
                            </div>
                            <div>
                                <label className="op-label text-[10px] text-t8f95a3">Cifra (€)</label>
                                <input
                                    type="number" min="0" value={mFee}
                                    onChange={(e) => setMFee(e.target.value)}
                                    className="w-full bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-1.5 mt-1 focus:border-t2bb6ff outline-none op-mono"
                                    data-testid="manual-fee"
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={addManual} disabled={resolvingManual}
                                    className="op-btn op-btn-primary text-sm w-full"
                                    data-testid="manual-add-btn"
                                >
                                    {resolvingManual ? "Cerco…" : "Aggiungi riga"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {loading && <div className="op-label text-t8f95a3 animate-pulse mt-6">// PARSING…</div>}
                    {error && <div className="text-tff4d4d text-sm mt-4">{error}</div>}

                    {(preview || extraRows.length > 0) && (
                        <div className="mt-6">
                            {/* Stats */}
                            <div className="grid grid-cols-4 gap-3 op-card p-4">
                                <Stat label="Totale righe" value={allRows.length} />
                                <Stat label="Pronte" value={allRows.filter((r) => r.status === "ready").length} color="rgb(var(--tc-7ce38b))" />
                                <Stat label="Errori" value={allRows.filter((r) => r.status === "error").length} color="rgb(var(--tc-ff4d4d))" />
                                <Stat label="Selezionate" value={totalSelected} color="rgb(var(--tc-2bb6ff))" />
                            </div>

                            <div className="flex items-center gap-2 mt-4">
                                <button onClick={() => toggleAll(true)} className="op-btn op-btn-ghost text-xs" data-testid="select-all">
                                    Seleziona tutte
                                </button>
                                <button onClick={() => toggleAll(false)} className="op-btn op-btn-ghost text-xs" data-testid="deselect-all">
                                    Deseleziona tutte
                                </button>
                                <div className="ml-auto op-mono text-[11px] text-tff7a1a">
                                    Cifra totale: € {totalCash.toLocaleString("it-IT")}
                                </div>
                            </div>

                            <div className="op-card overflow-hidden mt-3">
                                <table className="min-w-full text-xs">
                                    <thead className="bg-t0e1014 border-b border-t22252a">
                                        <tr>
                                            <th className="px-2 py-3 w-10"></th>
                                            <th className="px-3 py-3 text-left op-label text-[10px] text-tff7a1a">Giocatore</th>
                                            <th className="px-3 py-3 text-left op-label text-[10px] text-tff7a1a">Proprietario</th>
                                            <th className="px-3 py-3 text-left op-label text-[10px] text-tff7a1a">Va a</th>
                                            <th className="px-3 py-3 text-right op-label text-[10px] text-tff7a1a">Cifra</th>
                                            <th className="px-3 py-3 text-center op-label text-[10px] text-tff7a1a">Tipo</th>
                                            <th className="px-3 py-3 text-left op-label text-[10px] text-tff7a1a">Note</th>
                                            <th className="px-3 py-3 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allRows.map((r, ridx) => {
                                            const isReturn = r.fee === 0;
                                            const isManual = String(r.idx).startsWith("m-");
                                            const key = keyFor(r);
                                            return (
                                                <tr key={key}
                                                    className={`border-b border-t16181c ${ridx % 2 === 0 ? "bg-t0a0b0e" : "bg-t0d0e12"} ${r.status === "error" ? "opacity-50" : ""} ${isManual ? "border-l-2 border-l-t2bb6ff" : ""}`}
                                                    data-testid={`obblighi-row-${r.idx}`}>
                                                    <td className="px-2 py-2 text-center">
                                                        <input type="checkbox"
                                                            checked={!!selected[key]}
                                                            onChange={() => toggleRow(key)}
                                                            disabled={r.status !== "ready"}
                                                            className="accent-tff7a1a w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
                                                            data-testid={`obblighi-check-${r.idx}`}/>
                                                    </td>
                                                    <td className="px-3 py-2 text-tffffff">
                                                        {r.raw_player}
                                                        {isManual && <span className="op-label text-[8px] text-t2bb6ff ml-1">[MAN]</span>}
                                                    </td>
                                                    <td className="px-3 py-2 text-tc9ccd3">{r.owner_team_name || <span className="text-t5c6370">—</span>}</td>
                                                    <td className="px-3 py-2 text-tc9ccd3">{r.host_team_name || <span className="text-t5c6370">{r.raw_team}</span>}</td>
                                                    <td className="px-3 py-2 text-right op-mono">
                                                        {isReturn ? <span className="text-t7ce38b">Gratis</span> : <span className="text-tff7a1a">{fmtEur(r.fee)}</span>}
                                                    </td>
                                                    <td className="px-3 py-2 text-center">
                                                        {isReturn
                                                            ? <span className="op-label text-[9px] px-2 py-0.5 border border-t7ce38b/40 text-t7ce38b">RIENTRO</span>
                                                            : <span className="op-label text-[9px] px-2 py-0.5 border border-tff7a1a/40 text-tff7a1a">RISCATTO</span>}
                                                    </td>
                                                    <td className="px-3 py-2 text-tff4d4d op-mono text-[10px]">{r.note}</td>
                                                    <td className="px-2 py-2 text-center">
                                                        {isManual && (
                                                            <button onClick={() => removeManual(r.idx)}
                                                                className="op-btn op-btn-ghost text-xs px-2"
                                                                data-testid={`manual-remove-${r.idx}`}>✕</button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <button onClick={apply} disabled={applying || totalSelected === 0}
                                className="op-btn op-btn-primary mt-6"
                                data-testid="apply-obblighi">
                                {applying ? "Applico…" : `Applica ${totalSelected} trasferimenti`}
                            </button>
                        </div>
                    )}

                    {applyResult && (
                        <div className="op-card p-5 mt-6" style={{ borderLeft: "3px solid rgb(var(--tc-7ce38b))" }} data-testid="apply-result">
                            <div className="op-label text-[10px] text-t7ce38b mb-2">// COMPLETATO</div>
                            <div className="text-tffffff text-lg">
                                ✅ {applyResult.applied} applicati
                                {applyResult.failed > 0 && <span className="text-tff4d4d"> · ❌ {applyResult.failed} falliti</span>}
                            </div>
                            <div className="op-mono text-[11px] text-t8f95a3 mt-2">
                                Vai allo "Storico & Rollback" se devi annullare singole operazioni.
                            </div>
                            <ul className="mt-3 space-y-1 text-xs op-mono text-tc9ccd3 max-h-64 overflow-y-auto">
                                {applyResult.results.map((r, i) => (
                                    <li key={i} className={r.ok ? "" : "text-tff4d4d"}>
                                        {r.ok
                                            ? `✓ ${r.player_name}: ${r.owner} → ${r.host} (${r.kind === "loan_return" ? "rientro" : `€${r.fee.toLocaleString("it-IT")}`})`
                                            : `✗ ${r.player_name}: ${r.error}`}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}

            {tab === "history" && (
                <div className="mt-5">
                    {historyLoading ? (
                        <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO STORICO…</div>
                    ) : history.length === 0 ? (
                        <div className="op-card p-8 text-center text-t8f95a3 text-sm">
                            Nessun trasferimento ancora applicato.
                        </div>
                    ) : (
                        <div className="op-card overflow-hidden">
                            <table className="min-w-full text-xs">
                                <thead className="bg-t0e1014 border-b border-t22252a">
                                    <tr>
                                        <th className="px-3 py-3 text-left op-label text-[10px] text-tff7a1a">Data</th>
                                        <th className="px-3 py-3 text-left op-label text-[10px] text-tff7a1a">Giocatore</th>
                                        <th className="px-3 py-3 text-left op-label text-[10px] text-tff7a1a">Da</th>
                                        <th className="px-3 py-3 text-left op-label text-[10px] text-tff7a1a">A</th>
                                        <th className="px-3 py-3 text-right op-label text-[10px] text-tff7a1a">Cifra</th>
                                        <th className="px-3 py-3 text-center op-label text-[10px] text-tff7a1a">Tipo</th>
                                        <th className="px-3 py-3 text-center op-label text-[10px] text-tff7a1a">Stato</th>
                                        <th className="px-3 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((h, idx) => (
                                        <tr key={h.id}
                                            className={`border-b border-t16181c ${idx % 2 === 0 ? "bg-t0a0b0e" : "bg-t0d0e12"} ${h.rolled_back ? "opacity-60" : ""}`}
                                            data-testid={`history-row-${h.id}`}>
                                            <td className="px-3 py-2 op-mono text-[10px] text-t5c6370">
                                                {new Date(h.applied_at).toLocaleString("it-IT")}
                                            </td>
                                            <td className="px-3 py-2 text-tffffff">{h.player_name}</td>
                                            <td className="px-3 py-2 text-tc9ccd3">{h.owner_team_name}</td>
                                            <td className="px-3 py-2 text-tc9ccd3">{h.host_team_name}</td>
                                            <td className="px-3 py-2 text-right op-mono">
                                                {h.fee === 0 ? <span className="text-t7ce38b">Gratis</span> : <span className="text-tff7a1a">{fmtEur(h.fee)}</span>}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <span className="op-label text-[9px] px-2 py-0.5 border"
                                                    style={{ borderColor: h.kind === "buy_obligation" ? "rgb(var(--tc-ff7a1a) / 0.4)" : "rgb(var(--tc-7ce38b) / 0.4)",
                                                             color: h.kind === "buy_obligation" ? "rgb(var(--tc-ff7a1a))" : "rgb(var(--tc-7ce38b))" }}>
                                                    {h.kind === "buy_obligation" ? "RISCATTO" : "RIENTRO"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                {h.rolled_back
                                                    ? <span className="op-label text-[9px] px-2 py-0.5 border border-t5c6370 text-t5c6370">ANNULLATO</span>
                                                    : <span className="op-label text-[9px] px-2 py-0.5 border border-t7ce38b/40 text-t7ce38b">ATTIVO</span>}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                {!h.rolled_back && (
                                                    <button onClick={() => rollback(h.id, h.player_name)}
                                                        className="op-btn op-btn-ghost text-[10px] text-tff4d4d"
                                                        data-testid={`rollback-${h.id}`}>
                                                        Annulla
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, color = "rgb(var(--tc-ff7a1a))" }) {
    return (
        <div className="px-2">
            <div className="op-label text-[9px]" style={{ color }}>{label}</div>
            <div className="op-mono text-2xl font-bold text-tffffff mt-1">{value}</div>
        </div>
    );
}

function TabBtn({ active, onClick, children, testId }) {
    return (
        <button
            onClick={onClick} data-testid={testId}
            className={`op-label text-xs px-4 py-2 border-b-2 transition-colors ${active ? "border-tff7a1a text-tffffff" : "border-transparent text-t8f95a3 hover:text-tffffff"}`}
        >
            {children}
        </button>
    );
}
